// src/components/roads/page/AnalyticsPanels.js
// Dashboard panels — extracted from RoadWorksPage v4.7.
// --- UPDATE: v5.0 — Condition panel rows are clickable filters; inspections show
// "in N days / overdue" and pause polling while the tab is hidden; AI engine panel
// carries the AI/ML iconography and per-endpoint latency colouring.
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Badge, CircularProgress, CircularProgressLabel, Divider, Flex, HStack, Icon,
    IconButton, Progress, Select, SimpleGrid, Spinner, Text, Tooltip, VStack,
    useColorModeValue, useInterval,
} from '@chakra-ui/react';
import {
    MdAssessment, MdLayers, MdStraighten, MdSchedule, MdRefresh, MdError,
} from 'react-icons/md';
import * as roadService from '../../../services/roadService';
import roadAIEngineService from '../../../services/roadAIEngineService';
import {
    ANIM, CONDITION_CFG, EmptyState, MotionBox, PanelHeader, SectionCard, ShimmerSkeleton,
} from './shared';
import { AiChip, AiChipIcon } from './AiMlIcons';

// ─────────────────────────────────────────────────────────────────────────────
// CONDITION DISTRIBUTION PANEL
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE: optional activeCondition / onSelectCondition turn rows into filters
export const ConditionDistributionPanel = memo(({ data, isLoading, activeCondition, onSelectCondition }) => {
// --- END UPDATE
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const trackColor  = useColorModeValue('gray.100','gray.700');
    const barRowHover = useColorModeValue('gray.50', 'gray.750');
    const activeBg    = useColorModeValue('blue.50', 'whiteAlpha.100');

    const total = useMemo(() =>
        (data || []).reduce((s, d) => s + (d.count || 0), 0), [data]);

    const rows = useMemo(() => {
        const order = ['excellent','good','fair','poor','critical','unrated'];
        const map = (data || []).reduce((acc, d) => {
            acc[d.rating || 'unrated'] = d.count || 0; return acc;
        }, {});
        return order.map(k => ({
            key: k,
            label: CONDITION_CFG[k]?.label || 'Unrated',
            color: CONDITION_CFG[k]?.color || 'gray',
            count: map[k] || 0,
            pct: total > 0 ? ((map[k] || 0) / total) * 100 : 0,
        }));
    }, [data, total]);

    return (
        <SectionCard accentColor="blue">
            <PanelHeader
                iconBg="blue.500" icon={MdAssessment}
                title="Condition Distribution"
                subtitle={onSelectCondition ? 'Network-wide PCI breakdown · click to filter' : 'Network-wide PCI breakdown'}
                right={
                    <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="xs">
                        {total} rated
                    </Badge>
                }
            />
            <Box p={4}>
                {isLoading ? (
                    <ShimmerSkeleton lines={5} />
                ) : total === 0 ? (
                    <EmptyState icon={MdAssessment} title="No assessments yet"
                        subtitle="Record condition assessments to see distribution" />
                ) : (
                    <VStack spacing={2.5} align="stretch">
                        {rows.map((r, i) => {
                            const clickable = onSelectCondition && r.key !== 'unrated' && r.count > 0;
                            const isActive = activeCondition === r.key;
                            return (
                                <MotionBox key={r.key}
                                    {...ANIM.fadeInLeft} transition={ANIM.stagger(i, 0.06)}
                                >
                                    <Box _hover={{ bg: barRowHover }} p={2} borderRadius="lg"
                                        transition="all 0.15s"
                                        // --- UPDATE: row filters the inventory
                                        as={clickable ? 'button' : 'div'} w="100%" textAlign="left"
                                        bg={isActive ? activeBg : undefined}
                                        cursor={clickable ? 'pointer' : 'default'}
                                        onClick={clickable ? () => onSelectCondition(isActive ? '' : r.key) : undefined}
                                        aria-pressed={clickable ? isActive : undefined}
                                        // --- END UPDATE
                                    >
                                        <HStack justify="space-between" mb={1}>
                                            <HStack spacing={2}>
                                                <Box w={2} h={2} borderRadius="full"
                                                    bg={`${r.color}.500`}
                                                    boxShadow={`0 0 6px var(--chakra-colors-${r.color}-400)`} />
                                                <Text fontSize="xs" fontWeight="700">{r.label}</Text>
                                            </HStack>
                                            <HStack spacing={2}>
                                                <Text fontSize="2xs" color={mutedColor}>
                                                    {r.pct.toFixed(1)}%
                                                </Text>
                                                <Text fontSize="xs" fontWeight="800" color={`${r.color}.500`}>
                                                    {r.count}
                                                </Text>
                                            </HStack>
                                        </HStack>
                                        <Progress value={r.pct} size="sm" colorScheme={r.color}
                                            borderRadius="full" bg={trackColor}
                                            hasStripe={r.key === 'critical' || r.key === 'poor'}
                                            isAnimated={r.key === 'critical' || r.key === 'poor'} />
                                    </Box>
                                </MotionBox>
                            );
                        })}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
});
ConditionDistributionPanel.displayName = 'ConditionDistributionPanel';

// ─────────────────────────────────────────────────────────────────────────────
// SURFACE TYPE BREAKDOWN PANEL
// ─────────────────────────────────────────────────────────────────────────────
const SURFACE_COLORS = {
    asphalt:'gray', concrete:'blue', gravel:'orange',
    dirt:'yellow', paved_block:'purple', chip_seal:'teal', unpaved:'red',
};

export const SurfaceTypePanel = memo(({ data, isLoading }) => {
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const itemBg      = useColorModeValue('gray.50', 'gray.750');
    const borderColor = useColorModeValue('gray.200','gray.700');

    // --- UPDATE: share of total length per surface
    const totalKm = useMemo(
        () => (data || []).reduce((s, d) => s + (Number(d.total_length_km) || 0), 0),
        [data]
    );
    // --- END UPDATE

    return (
        <SectionCard accentColor="purple">
            <PanelHeader
                iconBg="purple.500" icon={MdLayers}
                title="Surface Composition"
                subtitle={totalKm > 0 ? `Network surface type distribution · ${totalKm.toFixed(1)} km` : 'Network surface type distribution'}
            />
            <Box p={4}>
                {isLoading ? (
                    <ShimmerSkeleton lines={4} />
                ) : !data || data.length === 0 ? (
                    <EmptyState icon={MdLayers} title="No surface data"
                        subtitle="Surface types will populate as roads are added" />
                ) : (
                    <SimpleGrid columns={2} spacing={3}>
                        {data.slice(0, 6).map((s, i) => {
                            const color = SURFACE_COLORS[s.surface] || 'gray';
                            const sharePct = totalKm > 0 ? ((Number(s.total_length_km) || 0) / totalKm) * 100 : 0;
                            return (
                                <MotionBox key={s.surface || `surface-${i}`}
                                    {...ANIM.scaleIn} transition={ANIM.stagger(i, 0.05)}
                                >
                                    <Box p={3} bg={itemBg} borderRadius="xl"
                                        borderWidth="1px" borderColor={borderColor}
                                        _hover={{ borderColor:`${color}.300`, transform:'scale(1.02)',
                                            boxShadow:`0 4px 16px var(--chakra-colors-${color}-100)` }}
                                        transition="all 0.25s">
                                        <HStack justify="space-between" mb={1}>
                                            <Text fontSize="2xs" color={mutedColor} fontWeight="600"
                                                textTransform="uppercase" letterSpacing="wider" noOfLines={1}>
                                                {(s.surface || 'unknown').replace(/_/g, ' ')}
                                            </Text>
                                            <Icon as={MdStraighten} boxSize={3.5} color={`${color}.400`} />
                                        </HStack>
                                        <HStack spacing={1} align="baseline">
                                            <Text fontSize="2xl" fontWeight="900" color={`${color}.500`} lineHeight="1">
                                                {s.count}
                                            </Text>
                                            <Text fontSize="2xs" color={mutedColor}>roads</Text>
                                        </HStack>
                                        <Text fontSize="2xs" color={mutedColor} mt={1}>
                                            {(s.total_length_km || 0).toFixed(1)} km
                                            {/* --- UPDATE */}
                                            {sharePct > 0 ? ` · ${sharePct.toFixed(0)}% of length` : ''}
                                            {/* --- END UPDATE */}
                                        </Text>
                                        {/* --- UPDATE */}
                                        {sharePct > 0 && (
                                            <Progress value={sharePct} size="xs" colorScheme={color} borderRadius="full" mt={1.5} />
                                        )}
                                        {/* --- END UPDATE */}
                                    </Box>
                                </MotionBox>
                            );
                        })}
                    </SimpleGrid>
                )}
            </Box>
        </SectionCard>
    );
});
SurfaceTypePanel.displayName = 'SurfaceTypePanel';

// ─────────────────────────────────────────────────────────────────────────────
// UPCOMING INSPECTIONS PANEL
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE: relative due label
const dueLabel = (dateStr) => {
    const t = new Date(dateStr).getTime();
    if (!Number.isFinite(t)) return { text: '', color: 'gray' };
    const days = Math.round((t - Date.now()) / 86400000);
    if (days < 0) return { text: `${Math.abs(days)}d overdue`, color: 'red' };
    if (days === 0) return { text: 'today', color: 'orange' };
    if (days <= 3) return { text: `in ${days}d`, color: 'orange' };
    return { text: `in ${days}d`, color: 'cyan' };
};
// --- END UPDATE

export const UpcomingInspectionsPanel = memo(({ logger }) => {
    const [inspections, setInspections] = useState([]);
    const [isLoading,   setIsLoading]   = useState(true);
    const [windowDays,  setWindowDays]  = useState(30);

    const borderColor = useColorModeValue('gray.200','gray.700');
    const rowHover    = useColorModeValue('gray.50','gray.750');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const iconTileBg  = useColorModeValue('cyan.50','whiteAlpha.100');

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await roadService.getUpcomingInspections({ days: windowDays });
            setInspections(res?.data || []);
            logger?.info('Inspections loaded', { count: res?.data?.length });
        } catch (err) {
            logger?.error('Inspections load failed', err);
            setInspections([]);
        } finally {
            setIsLoading(false);
        }
    }, [windowDays, logger]);

    useEffect(() => { load(); }, [load]);
    // --- UPDATE: skip background polling while the tab is hidden
    useInterval(() => {
        if (typeof document === 'undefined' || document.visibilityState !== 'hidden') load();
    }, 120000);
    // --- END UPDATE

    return (
        <SectionCard accentColor="cyan">
            <PanelHeader
                iconBg="cyan.500" icon={MdSchedule}
                title="Upcoming Inspections"
                subtitle={`Next ${windowDays} days${inspections.length ? ` · ${inspections.length} scheduled` : ''}`}
                right={
                    <HStack spacing={2}>
                        <Select size="xs" value={windowDays}
                            onChange={e => setWindowDays(Number(e.target.value))}
                            w="80px" borderRadius="lg" fontSize="2xs" aria-label="Inspection window">
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                        </Select>
                        <Tooltip label="Refresh" hasArrow>
                            <IconButton icon={<Icon as={MdRefresh} />} size="xs" variant="ghost"
                                onClick={load} borderRadius="full" aria-label="Refresh inspections"
                                _hover={{ transform:'rotate(180deg)' }} transition="all 0.3s" />
                        </Tooltip>
                    </HStack>
                }
            />
            <Box maxH="320px" overflowY="auto" p={3} css={{
                '&::-webkit-scrollbar': { width:'4px' },
                '&::-webkit-scrollbar-thumb': { background:'var(--chakra-colors-gray-300)', borderRadius:'2px' },
            }}>
                {isLoading && inspections.length === 0 ? (
                    <VStack p={2} spacing={2}>
                        {[1,2,3].map(i => <ShimmerSkeleton key={i} lines={2} />)}
                    </VStack>
                ) : inspections.length === 0 ? (
                    <EmptyState icon={MdSchedule} title="No upcoming inspections"
                        subtitle={`No inspections scheduled in the next ${windowDays} days`} />
                ) : (
                    <VStack spacing={1.5} align="stretch">
                        {inspections.map((insp, i) => {
                            const due = dueLabel(insp.scheduled_date);
                            return (
                                <MotionBox key={insp.id || i}
                                    {...ANIM.slideIn} transition={ANIM.stagger(Math.min(i, 10), 0.04)}
                                >
                                    <Flex p={3} borderRadius="xl" borderWidth="1px"
                                        borderColor={due.color === 'red' ? 'red.200' : borderColor}
                                        _hover={{ borderColor:'cyan.300', bg: rowHover, transform:'translateX(2px)' }}
                                        transition="all 0.2s" align="center" gap={3}>
                                        <Box bg={iconTileBg} p={2} borderRadius="lg" flexShrink={0}>
                                            <Icon as={MdSchedule} color="cyan.600" boxSize={4} />
                                        </Box>
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="xs" fontWeight="700" noOfLines={1}>
                                                {insp.road_name || 'Unnamed road'}
                                            </Text>
                                            <HStack spacing={2} mt={0.5} flexWrap="wrap">
                                                <Badge colorScheme="cyan" fontSize="2xs" borderRadius="full">
                                                    {insp.inspection_type || 'routine'}
                                                </Badge>
                                                <Text fontSize="2xs" color={mutedColor}>
                                                    {new Date(insp.scheduled_date).toLocaleDateString()}
                                                </Text>
                                            </HStack>
                                        </Box>
                                        {/* --- UPDATE */}
                                        {due.text && (
                                            <Badge colorScheme={due.color} variant={due.color === 'red' ? 'solid' : 'subtle'} fontSize="2xs" borderRadius="full">
                                                {due.text}
                                            </Badge>
                                        )}
                                        {/* --- END UPDATE */}
                                    </Flex>
                                </MotionBox>
                            );
                        })}
                    </VStack>
                )}
            </Box>
        </SectionCard>
    );
});
UpcomingInspectionsPanel.displayName = 'UpcomingInspectionsPanel';

// ─────────────────────────────────────────────────────────────────────────────
// SYSTEM HEALTH PANEL — Powered by RoadAIEngineService Event Bus
// ─────────────────────────────────────────────────────────────────────────────
export const SystemHealthPanel = memo(({ logger }) => {
    const [healthReport, setHealthReport] = useState(null);

    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const trackColor  = useColorModeValue('gray.100','gray.700');
    const rowHoverBg  = useColorModeValue('gray.50', 'gray.750');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    useEffect(() => {
        const initialHealth = roadAIEngineService.getHealthReport();
        if (initialHealth) setHealthReport(initialHealth);

        const unsubscribe = roadAIEngineService.on('health:change', (report) => {
            logger?.info('System Health Changed', report);
            setHealthReport(report);
        });

        return () => unsubscribe();
    }, [logger]);

    const endpoints    = healthReport?.endpoints || [];
    const healthyCount = endpoints.filter(e => e.status === 'ok').length;
    const healthPct    = endpoints.length > 0
        ? Math.round((healthyCount / endpoints.length) * 100)
        : 100;

    const isCircuitOpen = healthReport?.circuit?.state === 'OPEN';

    return (
        <SectionCard accentColor={isCircuitOpen ? 'red' : 'teal'}>
            <PanelHeader
                iconBg={isCircuitOpen ? 'red.500' : 'teal.500'}
                // --- UPDATE: AI chip icon + ML badge
                icon={isCircuitOpen ? MdError : AiChipIcon}
                badge={<AiChip kind="ml" size="xs" tooltip="Health of the FlowSense ML microservice and its API routes" />}
                // --- END UPDATE
                title="AI Engine & Network Health"
                subtitle={`Circuit: ${healthReport?.circuit?.state || 'CLOSED'} | Queue: ${healthReport?.offlineQueue || 0}`}
                right={
                    <MotionBox
                        animate={{ rotate: healthPct === 100 ? 0 : [0,5,-5,0] }}
                        transition={{ duration:2, repeat: healthPct < 100 ? Infinity : 0 }}
                    >
                        <CircularProgress value={healthPct}
                            color={healthPct === 100 ? 'green.400' : 'orange.400'}
                            size="48px" trackColor={trackColor}>
                            <CircularProgressLabel fontSize="xs" fontWeight="800">{healthPct}%</CircularProgressLabel>
                        </CircularProgress>
                    </MotionBox>
                }
            />
            <VStack px={4} py={3} spacing={1} align="stretch" maxH="180px" overflowY="auto">
                {endpoints.length === 0 ? (
                    <Text fontSize="xs" color={mutedColor} textAlign="center" py={4}>
                        Probing services…
                    </Text>
                ) : endpoints.map((ep, i) => {
                    // --- UPDATE: latency colouring
                    const latencyColor = ep.latencyMs > 1500 ? 'red.400' : ep.latencyMs > 600 ? 'orange.400' : mutedColor;
                    // --- END UPDATE
                    return (
                        <MotionBox key={ep.path} {...ANIM.fadeInLeft} transition={ANIM.stagger(i, 0.06)}>
                            <Flex align="center" justify="space-between" py={1.5}
                                _hover={{ bg: rowHoverBg }}
                                transition="all 0.15s" borderRadius="lg" px={2}>
                                <HStack spacing={2}>
                                    <Box w={2} h={2} borderRadius="full"
                                        bg={ep.status === 'ok' ? 'green.500' : 'red.500'}
                                        boxShadow={ep.status === 'ok'
                                            ? '0 0 6px var(--chakra-colors-green-400)'
                                            : '0 0 6px var(--chakra-colors-red-400)'} />
                                    <Text fontSize="xs" fontWeight={ep.status === 'ok' ? '600' : '400'}
                                        noOfLines={1} maxW="140px">
                                        {ep.path.split('?')[0]}
                                    </Text>
                                </HStack>
                                <HStack spacing={2}>
                                    {ep.status !== 'ok' ? (
                                        <Spinner size="xs" color="red.400" />
                                    ) : (
                                        <Text fontSize="2xs" color={latencyColor} fontFamily="mono">{ep.latencyMs}ms</Text>
                                    )}
                                    <Badge colorScheme={ep.status === 'ok' ? 'green' : 'red'}
                                        fontSize="2xs" borderRadius="full">
                                        {ep.status === 'ok' ? 'OK' : 'DOWN'}
                                    </Badge>
                                </HStack>
                            </Flex>
                        </MotionBox>
                    );
                })}
            </VStack>
            <Box px={4} pb={4} pt={1}>
                <Divider mb={3} borderColor={borderColor} />
                <Flex justify="space-between" mb={1}>
                    <Text fontSize="2xs" color={mutedColor} fontWeight="600">OVERALL HEALTH</Text>
                    <Text fontSize="2xs" fontWeight="800"
                        color={healthPct === 100 ? 'green.500' : 'orange.400'}>
                        {healthyCount}/{endpoints.length || '…'} services
                    </Text>
                </Flex>
                <Progress value={healthPct}
                    colorScheme={healthPct === 100 ? 'green' : 'orange'}
                    size="sm" borderRadius="full" bg={trackColor}
                    hasStripe isAnimated />
            </Box>
        </SectionCard>
    );
});
SystemHealthPanel.displayName = 'SystemHealthPanel';
